import { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { MessageSquare, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import { Button } from '@/components/Button';
import { useMessaging } from '@/messaging/useMessaging';
import { ConversationThread } from './ConversationThread';
import { RecipientPicker } from './RecipientPicker';
import styles from './MessagingInbox.module.css';

export const MessagingInbox = ({
  conversationId,
  onConversationChange,
  isComposing,
  onComposingChange,
}: {
  conversationId?: string | null;
  onConversationChange?: (id: string | null) => void;
  /** Optional route-controlled recipient picker state. Opening it never creates a conversation. */
  isComposing?: boolean;
  onComposingChange?: (open: boolean) => void;
}) => {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const { api, scope, bootstrap, connection, unreadCount, isError, refresh } =
    useMessaging();
  const [selected, setSelected] = useState<string | null>(
    conversationId ?? null
  );
  useEffect(() => {
    setSelected(conversationId ?? null);
    if (conversationId) setLocalComposing(false);
  }, [conversationId]);
  const select = (id: string | null) => {
    setSelected(id);
    onConversationChange?.(id);
  };
  const [localComposing, setLocalComposing] = useState(false);
  const composing = isComposing ?? localComposing;
  const setComposing = useCallback(
    (open: boolean) => {
      setLocalComposing(open);
      onComposingChange?.(open);
    },
    [onComposingChange]
  );
  const list = useInfiniteQuery({
    queryKey: [QueryKeys.MessagingConversations, scope],
    queryFn: ({ pageParam }) =>
      api.conversations({ cursor: pageParam ?? undefined, limit: 30 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!bootstrap,
    refetchInterval: 15_000,
    retry: 1,
    throwOnError: false,
  });
  const conversations = [
    ...new Map(
      (list.data?.pages.flatMap((page) => page.items) ?? []).map((row) => [
        row.id,
        row,
      ])
    ).values(),
  ];
  const [retryingSession, setRetryingSession] = useState(false);
  const sessionReady = !!bootstrap;
  const retrySession = async () => {
    setRetryingSession(true);
    try {
      await refresh();
    } catch {
      // The provider retains its specific connection error for another retry.
    } finally {
      setRetryingSession(false);
    }
  };
  return (
    <div className={styles.page}>
      <header className={styles.heading}>
        <div>
          <h1>{t('messaging.title')}</h1>
          <p>{t('messaging.subtitle')}</p>
        </div>
        <Button
          IconPrefix={Plus}
          onClick={() => {
            select(null);
            setComposing(true);
          }}
        >
          {t('messaging.new_message')}
        </Button>
      </header>
      {!sessionReady && (
        <div
          role={isError && !retryingSession ? 'alert' : 'status'}
          className={styles.banner}
        >
          <span>
            {t(
              isError && !retryingSession
                ? 'messaging.connection_error'
                : 'messaging.loading'
            )}
          </span>
          {isError && (
            <Button
              onClick={() => void retrySession()}
              disabled={retryingSession}
            >
              {t(retryingSession ? 'messaging.syncing' : 'messaging.retry')}
            </Button>
          )}
        </div>
      )}
      {sessionReady && connection !== 'connected' && (
        <div role="status" className={styles.banner}>
          {t(
            connection === 'offline'
              ? 'messaging.offline'
              : 'messaging.reconnecting'
          )}
          {connection === 'connecting' && (
            <Button
              variant="outline"
              onClick={() => {
                void refresh();
              }}
            >
              {t('messaging.retry')}
            </Button>
          )}
        </div>
      )}
      <div
        className={`${styles.workspace} ${selected || composing ? styles.active : ''}`}
      >
        <aside
          className={styles.rail}
          aria-label={t('messaging.conversations')}
        >
          <div className={styles.railHeader}>
            <h2>{t('messaging.conversations')}</h2>
            {unreadCount > 0 && (
              <span
                className={styles.count}
                aria-label={t('messaging.unread', { count: unreadCount })}
              >
                {unreadCount}
              </span>
            )}
          </div>
          {sessionReady && list.isLoading && (
            <div className={styles.state} role="status">
              {t('messaging.loading')}
            </div>
          )}
          {sessionReady && list.isError && (
            <div className={styles.state} role="alert">
              <p>{t('messaging.conversations_error')}</p>
              <Button
                onClick={() => {
                  void list.refetch();
                }}
              >
                {t('messaging.retry')}
              </Button>
            </div>
          )}
          {sessionReady &&
            !list.isLoading &&
            !list.isError &&
            conversations.length === 0 && (
              <div className={styles.state}>
                <MessageSquare size={28} aria-hidden />
                <h2>{t('messaging.empty_title')}</h2>
                <p>{t('messaging.empty_body')}</p>
              </div>
            )}
          {sessionReady &&
            conversations.map((row) => (
              <button
                type="button"
                key={row.id}
                className={`${styles.conversation} ${selected === row.id ? styles.selected : ''}`}
                aria-current={selected === row.id ? 'true' : undefined}
                onClick={() => {
                  setComposing(false);
                  select(row.id);
                }}
              >
                <span className={styles.avatar} aria-hidden>
                  {row.peer.displayName
                    .trim()
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((word) => word[0])
                    .join('')}
                </span>
                <span className={styles.conversationText}>
                  <span className={styles.personName}>
                    {row.peer.displayName}
                  </span>
                  <span className={styles.preview}>
                    {row.lastMessage?.text || t('messaging.empty_messages')}
                  </span>
                </span>
                {row.unreadCount > 0 && (
                  <span
                    className={styles.count}
                    aria-label={t('messaging.unread', {
                      count: row.unreadCount,
                    })}
                  >
                    {row.unreadCount}
                  </span>
                )}
              </button>
            ))}
          {sessionReady && list.hasNextPage && (
            <Button
              variant="outline"
              disabled={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              {t('messaging.more_conversations')}
            </Button>
          )}
        </aside>
        <section className={styles.thread}>
          {composing ? (
            <>
              <div className={styles.threadHeader}>
                <Button variant="outline" onClick={() => setComposing(false)}>
                  {t('messaging.back')}
                </Button>
                <h2>{t('messaging.new_message')}</h2>
              </div>
              {sessionReady ? (
                <RecipientPicker
                  onSelected={(id) => {
                    setComposing(false);
                    select(id);
                    void list.refetch();
                  }}
                />
              ) : (
                <div className={styles.state}>
                  <p>{t('messaging.picker_waiting')}</p>
                </div>
              )}
            </>
          ) : selected && sessionReady ? (
            <ConversationThread
              key={`${scope}:${selected}`}
              id={selected}
              onBack={() => select(null)}
            />
          ) : (
            <div className={styles.state}>
              <MessageSquare size={36} aria-hidden />
              <h2>{t('messaging.empty_thread')}</h2>
              <p>{t('messaging.empty_thread_body')}</p>
            </div>
          )}
        </section>
      </div>
      <p className={styles.muted}>{t('messaging.privacy')}</p>
    </div>
  );
};
